import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Star, Sparkles, Check, MessageSquare, Settings, Link as LinkIcon, Bot, TrendingUp, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewsPage = () => {
  const { userProfile, getToken } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);
  const [aiSettings, setAiSettings] = useState({
    tone: 'professional',
    response_length: 'medium',
    include_name: true,
    auto_approve: false,
    language: 'fr'
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [todayStats, setTodayStats] = useState({ total_responses: 0, auto_responses: 0 });

  useEffect(() => {
    fetchReviews();
    fetchTodayStats();
  }, []);

  const fetchReviews = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${API}/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (error) {
      toast.error('Échec du chargement des avis');
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayStats = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${API}/reviews/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayStats({
        total_responses: response.data.auto_responses || 0,
        auto_responses: response.data.approved_responses || 0
      });
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleConnectGMB = () => {
    if (userProfile?.google_my_business_url) {
      window.open(userProfile.google_my_business_url, '_blank');
      toast.success('Ouverture de votre page Google My Business');
    } else {
      toast.info('Veuillez d\'abord configurer votre lien Google My Business dans les paramètres');
      setTimeout(() => navigate('/dashboard/settings'), 1500);
    }
  };

  const handleSaveAISettings = () => {
    toast.success('Paramètres IA sauvegardés!');
    setSettingsOpen(false);
  };

  const handleGenerateAIResponse = async (reviewId) => {
    try {
      const token = await getToken();
      const response = await axios.post(
        `${API}/reviews/${reviewId}/generate-response`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the review in state
      setReviews(reviews.map(r => 
        r.id === reviewId ? { ...r, ai_response: response.data.ai_response } : r
      ));
      
      toast.success('Réponse IA générée!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de la génération');
    }
  };

  const handleApproveResponse = async (reviewId, aiResponse) => {
    try {
      const token = await getToken();
      await axios.post(
        `${API}/reviews/${reviewId}/approve`,
        { ai_response: aiResponse },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReviews(reviews.map(r => 
        r.id === reviewId ? { ...r, response_status: 'approved' } : r
      ));
      
      toast.success('Réponse approuvée et postée!');
      setEditingReview(null);
    } catch (error) {
      toast.error('Échec de l\'approbation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2E59D7]"></div>
      </div>
    );
  }

  // Empty state with AI animation
  if (reviews.length === 0) {
    return (
      <div className="space-y-8" data-testid="reviews-page">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Réponses IA aux Avis</h2>
            <p className="text-gray-600">Connectez vos plateformes d'avis pour commencer</p>
          </div>
          
          <Button 
            onClick={handleConnectGMB}
            className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]" 
            data-testid="connect-gmb-button"
          >
            <LinkIcon className="w-4 h-4 mr-2" />
            Connecter Google My Business
          </Button>
        </div>

        {/* AI Stats Card */}
        <Card className="p-8 bg-gradient-to-br from-blue-50 to-sky-50 border-2 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Animated Bot Icon */}
              <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-br from-[#2E59D7] to-[#0082FB] rounded-2xl flex items-center justify-center animate-pulse">
                  <Bot className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Votre Assistant IA est Prêt!</h3>
                <p className="text-gray-600 mb-4">Connectez vos plateformes d'avis pour commencer à automatiser vos réponses</p>
                
                <div className="flex gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-[#2E59D7]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{todayStats.auto_responses}</p>
                      <p className="text-sm text-gray-600">Réponses générées</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#0082FB]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{todayStats.total_responses}</p>
                      <p className="text-sm text-gray-600">Total aujourd'hui</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Settings Button */}
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
              <DialogTrigger asChild>
                <Button 
                  size="lg"
                  className="bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200"
                  data-testid="configure-ai-button"
                >
                  <Settings className="w-5 h-5 mr-2" />
                  Configurer mon IA
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl">Paramètres de l'IA</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                  {/* Tone Selection */}
                  <div>
                    <Label className="text-base font-semibold mb-3 block">Ton des Réponses</Label>
                    <Select value={aiSettings.tone} onValueChange={(value) => setAiSettings({ ...aiSettings, tone: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professionnel</SelectItem>
                        <SelectItem value="friendly">Amical</SelectItem>
                        <SelectItem value="casual">Décontracté</SelectItem>
                        <SelectItem value="formal">Formel</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-gray-500 mt-1">Le style de communication de votre IA</p>
                  </div>

                  {/* Response Length */}
                  <div>
                    <Label className="text-base font-semibold mb-3 block">Longueur des Réponses</Label>
                    <Select value={aiSettings.response_length} onValueChange={(value) => setAiSettings({ ...aiSettings, response_length: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Courte (1-2 phrases)</SelectItem>
                        <SelectItem value="medium">Moyenne (2-3 phrases)</SelectItem>
                        <SelectItem value="long">Longue (3-5 phrases)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Language */}
                  <div>
                    <Label className="text-base font-semibold mb-3 block">Langue des Réponses</Label>
                    <Select value={aiSettings.language} onValueChange={(value) => setAiSettings({ ...aiSettings, language: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Español</SelectItem>
                        <SelectItem value="de">Deutsch</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Include Business Name */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-base font-semibold">Inclure le nom du business</Label>
                      <p className="text-sm text-gray-500">Mention du nom dans les réponses</p>
                    </div>
                    <button
                      onClick={() => setAiSettings({ ...aiSettings, include_name: !aiSettings.include_name })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        aiSettings.include_name ? 'bg-[#2E59D7]' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          aiSettings.include_name ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Auto Approve */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-base font-semibold">Approbation automatique</Label>
                      <p className="text-sm text-gray-500">Publier automatiquement les réponses générées</p>
                    </div>
                    <button
                      onClick={() => setAiSettings({ ...aiSettings, auto_approve: !aiSettings.auto_approve })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        aiSettings.auto_approve ? 'bg-[#2E59D7]' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          aiSettings.auto_approve ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setSettingsOpen(false)} className="flex-1">
                    Annuler
                  </Button>
                  <Button onClick={handleSaveAISettings} className="flex-1 bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]">
                    Enregistrer les Paramètres
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </Card>

        {/* How It Works */}
        <Card className="p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Comment ça marche?</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2E59D7] to-[#0082FB] rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                1
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Connectez vos plateformes</h4>
              <p className="text-sm text-gray-600">Liez Google My Business, Facebook, TripAdvisor</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2E59D7] to-[#0082FB] rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                2
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">L'IA génère des réponses</h4>
              <p className="text-sm text-gray-600">Réponses personnalisées basées sur le sentiment</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2E59D7] to-[#0082FB] rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                3
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Approuvez et publiez</h4>
              <p className="text-sm text-gray-600">Modifiez si nécessaire, puis publiez en un clic</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="reviews-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Réponses IA aux Avis</h2>
          <p className="text-gray-600">Gérez et répondez aux avis avec l'assistance de l'IA</p>
        </div>
        
        <div className="flex gap-3">
          <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="configure-ai-button">
                <Settings className="w-4 h-4 mr-2" />
                Configurer l'IA
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl">Paramètres de l'IA</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Same settings as above */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Ton des Réponses</Label>
                  <Select value={aiSettings.tone} onValueChange={(value) => setAiSettings({ ...aiSettings, tone: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professionnel</SelectItem>
                      <SelectItem value="friendly">Amical</SelectItem>
                      <SelectItem value="casual">Décontracté</SelectItem>
                      <SelectItem value="formal">Formel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-base font-semibold mb-3 block">Longueur des Réponses</Label>
                  <Select value={aiSettings.response_length} onValueChange={(value) => setAiSettings({ ...aiSettings, response_length: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="short">Courte (1-2 phrases)</SelectItem>
                      <SelectItem value="medium">Moyenne (2-3 phrases)</SelectItem>
                      <SelectItem value="long">Longue (3-5 phrases)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-base font-semibold mb-3 block">Langue des Réponses</Label>
                  <Select value={aiSettings.language} onValueChange={(value) => setAiSettings({ ...aiSettings, language: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="text-base font-semibold">Inclure le nom du business</Label>
                    <p className="text-sm text-gray-500">Mention du nom dans les réponses</p>
                  </div>
                  <button
                    onClick={() => setAiSettings({ ...aiSettings, include_name: !aiSettings.include_name })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      aiSettings.include_name ? 'bg-[#2E59D7]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        aiSettings.include_name ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setSettingsOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button onClick={handleSaveAISettings} className="flex-1 bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]">
                  Enregistrer les Paramètres
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button 
            onClick={handleConnectGMB}
            className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]" 
            data-testid="connect-gmb-button"
          >
            <LinkIcon className="w-4 h-4 mr-2" />
            Connecter Plateforme
          </Button>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-6">
        {reviews.length === 0 ? (
          <Card className="p-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun avis pour le moment. Ajoutez un avis pour commencer !</p>
          </Card>
        ) : (
          reviews.map((review) => (
            <Card key={review.id} className="p-6" data-testid={`review-card-${review.id}`}>
              <div className="space-y-4">
                {/* Review Header */}
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold capitalize">
                        {review.platform}
                      </span>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{review.review_text}</p>
                  </div>
                  
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    review.response_status === 'approved' ? 'bg-green-100 text-green-800' :
                    review.response_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {review.response_status}
                  </span>
                </div>

                {/* AI Response Section */}
                {review.ai_response ? (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-semibold text-cyan-900 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Réponse générée par IA
                      </p>
                      {editingReview === review.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingReview(null)}
                        >
                          Annuler
                        </Button>
                      )}
                    </div>
                    
                    {editingReview === review.id ? (
                      <div className="space-y-3">
                        <Textarea
                          value={review.ai_response}
                          onChange={(e) => {
                            setReviews(reviews.map(r => 
                              r.id === review.id ? { ...r, ai_response: e.target.value } : r
                            ));
                          }}
                          rows={4}
                          className="bg-white"
                          data-testid={`edit-response-${review.id}`}
                        />
                        <Button
                          onClick={() => handleApproveResponse(review.id, review.ai_response)}
                          className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]"
                          data-testid={`approve-button-${review.id}`}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Approuver & Publier
                        </Button>
                      </div>
                    ) : (
                      <p className="text-gray-700 leading-relaxed">{review.ai_response}</p>
                    )}
                  </div>
                ) : null}

                {/* Action Buttons */}
                {review.response_status === 'pending' && (
                  <div className="flex gap-3">
                    {!review.ai_response ? (
                      <Button
                        onClick={() => handleGenerateAIResponse(review.id)}
                        className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]"
                        data-testid={`generate-ai-response-${review.id}`}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Générer une réponse IA
                      </Button>
                    ) : (
                      <>
                        <Button
                          onClick={() => setEditingReview(review.id)}
                          variant="outline"
                          data-testid={`edit-response-${review.id}`}
                        >
                          Modifier la réponse
                        </Button>
                        <Button
                          onClick={() => handleApproveResponse(review.id, review.ai_response)}
                          className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Approuver & Publier
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ReviewsPage;